import React, { useEffect, useRef, useState } from "react";
import { useAutosizeTextArea, useResizable } from "./hooks/useHookHelpAIChat";
import Thinking from "./HelpAIThinking";
import styles from "./HelpAI.module.css";
import imgFace from "../../../src/components/HelpAIChat/images/logo_ai.png";
import { encryptAES, decryptAES } from "@maxis/client-logic/utils/cryptoUtil";
import { getLocalStorageItem } from "@maxis/client-logic/utils/localStorageUtil";
import { LOCAL_STORAGE_KEYS } from "@maxis/client-logic/constants/enumerates";

const HelpAI = ({ children, openState }) => {
  const AUTH_ENCRYPTION = import.meta.env.VITE_AUTH === "true";

  const messagesContainerRef = useRef(null);
  const overlayRef = useRef();
  const textareaRef = useRef();
  const lastMessageRef = useRef(null);
  const dropdownRef = useRef(null);

  const [text, setText] = useState("");
  const [width, setWidth] = useState(400);
  const [messages, setMessages] = useState([]);
  const [stay, setStay] = useState(true);

  const [selectedDropdown, setSelectedDropdown] = useState("AI-MAXIS-v1");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const dropdownDescriptions = {
    "AI-MAXIS-v1": "Llama3.0 모델입니다.",
    "AI-MAXIS-v2": "개선된 문맥 이해력을 가지고 있습니다.",
    "AI-MAXIS-v3": "고도화된 분석 및 요약 기능을 지원합니다.",
  };

  useAutosizeTextArea(textareaRef, text, { minHeight: 40, maxHeight: 200 });

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const pageFunctions = {
    search: async () => {
      try {
        if (!text || !stay) return;

        const userMessage = { id: "id", content: text, role: "user" };
        const assistantPlaceholder = {
          id: null,
          content: "",
          role: "assistant",
        };

        let updatedList;
        setMessages((prev) => {
          updatedList = [...prev, userMessage, assistantPlaceholder];
          return updatedList;
        });

        setText("");
        setStay(false);
        await new Promise((resolve) => setTimeout(resolve, 0));

        const headers = { "Content-Type": "application/json" };
        pageFunctions.setAuthorizationHeader(headers);

        const data = await pageFunctions.handleEncryption(updatedList);

        const response = await fetch(
          import.meta.env.VITE_HELP_AI_CHAT_URL ||
            "http://localhost:8002/api/help/tryChat",
          {
            method: "POST",
            headers: headers,
            body: data,
          }
        );

        if (!response.body) return;

        const [stream1, stream2] = response.body.tee();
        const nomal = new Response(stream1);

        let descrypto, reader;
        const decoder = new TextDecoder();
        if (!AUTH_ENCRYPTION) {
          reader = nomal.body.getReader();
        } else {
          const crypto = await new Response(stream2).json();
          descrypto = await decryptAES(crypto.EncryptData, crypto.iv);
        }

        let errCnt = 0;
        while (true) {
          let chunk;
          if (!AUTH_ENCRYPTION) {
            const { value, done } = await reader.read();
            if (done) break;
            chunk = decoder.decode(value, { stream: true });
          } else {
            chunk = descrypto;
          }

          for (const line of chunk.split("\n")) {
            if (errCnt) {
              setStay(true);
              return;
            }
            let trimmed = line.trim();
            if (trimmed.startsWith("data:")) {
              trimmed = trimmed.replace("data: ", "");
            }
            if (!trimmed) continue;
            const jsonStr = trimmed;
            if (jsonStr === "[DONE]") continue;

            let parsed, id, delta;
            try {
              parsed = JSON.parse(jsonStr);
              const finish_reason = parsed?.choices?.[0]?.finish_reason || "";
              if (finish_reason === "stop") {
                setStay(true);
                return;
              }
              if (parsed.responseCode) {
                id = "system";
                delta = parsed.responseText || "서버와 통신에 문제가 있습니다.";
                errCnt++;
                setStay(true);
              } else {
                id = parsed.id;
                delta = parsed.choices?.[0]?.delta?.content;
                if (!delta) continue;
                // for (const char of delta) {
                //   if (lastMessageRef.current) {
                //     lastMessageRef.current.textContent += char;
                //   }
                // }
                await new Promise(requestAnimationFrame);
              }
              pageFunctions.setMessagesData(id, delta);
            } catch {
              continue;
            }
          }
        }
      } catch (err) {
        const id = "system";
        const delta = "서버와 통신에 문제가 있습니다.";
        setStay(true);
        pageFunctions.setMessagesData(id, delta);
        console.log("err", err);
      }
    },
    /**
     * 설정 객체(settings)에 Authorization 헤더(토큰)를 추가
     *
     * @param {Object} settings - fetch 요청 옵션 (헤더가 포함됨)
     */
    setAuthorizationHeader: async (settings) => {
      const accessToken = getLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
      if (accessToken) {
        settings["Authorization"] = `Bearer ${accessToken}`;
      }
    },
    handleEncryption: async (data = []) => {
      if (AUTH_ENCRYPTION) {
        const { encryptData, gIv: iv } = await encryptAES(JSON.stringify(data));
        return JSON.stringify({ EncryptData: encryptData, iv: iv });
      }
      return JSON.stringify(data);
    },
    setMessagesData: (id = "", delta = "") => {
      setMessages((prev) => {
        const lastIdx = prev.length - 1;
        const lastMsg = prev[lastIdx];
        const msgId = lastMsg.id ?? id;
        const newContent = (lastMsg.content || "") + delta;
        return [
          ...prev.slice(0, lastIdx),
          { id: msgId, content: newContent, role: "assistant" },
        ];
      });
    },
    _onKeyUp: (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        pageFunctions.search();
      }
    },
  };

  return (
    <aside
      ref={overlayRef}
      className={`${styles.overlay} ${styles.open}`}
      style={{ width }}
    >
      <div className={styles.oerlay_cont}>
        <div className={styles.resizerLeft} />
        <header className={styles.header}>
          <div className={styles.header_title}>
            <div className={styles.header_wrap}>
              <div className={styles.ai_type_logo}></div>
              <div className={styles.ai_type}>
                <div className={styles.ai_type_dropdown} ref={dropdownRef}>
                  <div
                    className={styles.ai_type_dropdown_selected}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    {selectedDropdown}
                  </div>
                  {dropdownOpen && (
                    <div className={styles.ai_type_dropdown_list}>
                      {["AI-MAXIS-v1", "AI-MAXIS-v2", "AI-MAXIS-v3"].map(
                        (option, idx) => (
                          <div
                            key={idx}
                            className={styles.ai_type_dropdown_item}
                            onClick={() => {
                              setSelectedDropdown(option);
                              setDropdownOpen(false);
                            }}
                          >
                            <span>{option}</span>
                            {selectedDropdown === option && (
                              <span
                                style={{ float: "right", color: "#007aff" }}
                              >
                                <i className="bi bi-check-lg"></i>
                              </span>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
                <div className={styles.ai_type_txt}>
                  {dropdownDescriptions[selectedDropdown] ||
                    "Llama3.0 모델입니다."}
                </div>
              </div>
            </div>
          </div>
          <button
            className={styles.closeBtn}
            onClick={() => openState(false)}
            aria-label="닫기"
          >
            ×
          </button>
        </header>
        <div className={styles.cont}>
          <div className={styles.banner}>
            <div className={styles.banner_list}>
              <button className={styles.btn_ban}>
                <i className="bi bi-gear" />
              </button>
              <button className={styles.btn_ban}>
                <i className="bi bi-window-sidebar" />
              </button>
              <button className={styles.btn_ban}>
                <i className="bi bi-pie-chart" />
              </button>
              <button className={styles.btn_ban}>
                <i className="bi bi-graph-up" />
              </button>
            </div>
          </div>
          <section ref={messagesContainerRef} className={styles.messages}>
            {children}
            {messages.map((v, i) => {
              const isLast =
                i === messages.length - 1 && v.role === "assistant";
              return v.id ? (
                v.role === "assistant" ? (
                  <div
                    key={i}
                    ref={isLast ? lastMessageRef : null}
                    className={`${styles.message} ${styles.other}`}
                  >
                    {/* <div className={styles.ai_title}><img src={imgFace} />AI MAXIS</div> */}
                    <div className={styles.ai_title}>AI MAXIS</div>
                    <div className={styles.ai_cont}>
                      <div>{v.content}</div>
                    </div>
                  </div>
                ) : (
                  <div
                    key={i}
                    ref={isLast ? lastMessageRef : null}
                    className={`${styles.message} ${styles.self}`}
                  >
                    <div>{v.content}</div>
                  </div>
                )
              ) : (
                <Thinking key={i} />
              );
            })}
          </section>
        </div>
        <footer className={styles.input}>
          <textarea
            ref={textareaRef}
            value={text}
            className={styles.textarea}
            placeholder="메시지를 입력하세요…"
            onChange={(e) => setText(e.target.value)}
            onKeyUp={pageFunctions._onKeyUp}
          />
          <button className={styles.sendBtn} onClick={pageFunctions.search} />
        </footer>
      </div>
    </aside>
  );
};

export default HelpAI;
